import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import {
  ArrowsPointingOutIcon, BeakerIcon, BoltIcon, CodeBracketIcon, CpuChipIcon, CircleStackIcon, DeviceTabletIcon, DocumentTextIcon, ArrowDownTrayIcon, CommandLineIcon, HeartIcon, BoltSlashIcon, ServerIcon, UserGroupIcon
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';

const Hypersuit: React.FC = () => {
  const navigate = useNavigate();

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  const sectionVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.2 } }
  };

  return (
    <>
      <Helmet>
        <title>Hypersuit by Infinity Cloud - L'IA au service du micro-mouvement</title>
        <meta name="description" content="Hypersuit - L'IA au service du micro-mouvement, de la micro-torque et de l'autonomie. Un projet artisanal pour restaurer la mobilité." />
      </Helmet>

      <style>
        {`
          @keyframes float {
            0% { transform: translateY(0px); }
            50% { transform: translateY(-10px); }
            100% { transform: translateY(0px); }
          }
          .float-animation {
            animation: float 3s ease-in-out infinite;
          }

          #hypersuit-page-container { background-color: #f9fafb !important; /* Tailwind bg-gray-50 */ }
        `}
      </style>

      <div id="hypersuit-page-container" className="bg-gray-50 min-h-screen">
        {/* Section 1: Bannière principale */}
        <motion.section
          className="relative pt-20 pb-16 text-center bg-gradient-to-br from-blue-500 to-indigo-600 text-white overflow-hidden"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
        >
          <div className="container mx-auto px-4 relative z-10">
            <motion.h1
              className="text-4xl md:text-6xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-white to-gray-300"
              initial={{ y: -30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.6 }}
            >
              <span className="text-blue-300">Hypersuit</span> by Infinity Cloud
            </motion.h1>
            <motion.h2
              className="text-2xl md:text-3xl font-light mb-6"
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.6 }}
            >
              L'IA au service du micro-mouvement, de la micro-torque et de l'autonomie
            </motion.h2>
            <motion.div
              className="flex justify-center mt-10"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.8, duration: 0.7 }}
            >
              <img
                src="/images/kits/hypersuit-hero.png"
                alt="Vue d'un prototype Hypersuit avec microcontrôleur et capteurs permettant de détecter les micro-mouvements"
                className="w-full max-w-xl h-auto rounded-lg shadow-2xl float-animation"
              />
            </motion.div>
          </div>
          {/* Decorative shapes */}
          <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0">
             <div className="absolute -top-1/4 -left-1/4 w-1/2 h-1/2 bg-blue-400 rounded-full opacity-20 blur-3xl"></div>
             <div className="absolute -bottom-1/4 -right-1/4 w-1/2 h-1/2 bg-indigo-500 rounded-full opacity-20 blur-3xl"></div>
          </div>
        </motion.section>

        {/* Section 2: Introduction */}
        <motion.section
          className="py-12 md:py-16 bg-white"
          variants={sectionVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <div className="container mx-auto px-4 text-center">
            <motion.h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4" variants={cardVariants}>
              Introduction : une idée née de <span className="text-blue-600">mon expérience personnelle</span>
            </motion.h2>
            <motion.p className="text-lg text-gray-600 max-w-3xl mx-auto mb-6" variants={cardVariants}>
              Cette aventure est née de mon histoire personnelle. Ayant moi-même, Minh-Tam Dang, fait face à des défis de mobilité, tout comme plusieurs de mes proches âgés, j'ai ressenti le besoin de créer une solution légère, abordable et entièrement artisanale. Plutôt que d'attendre les dispositifs complexes des grands hôpitaux, j'ai décidé de prendre les choses en main et d'explorer le machine learning embarqué.
            </motion.p>
            <motion.p className="text-lg text-gray-600 max-w-3xl mx-auto mb-10" variants={cardVariants}>
              Aujourd'hui, Hypersuit porte cette ambition humaine : redonner l'autonomie aux personnes comme moi, sans dépendre d'équipes pluridisciplinaires coûteuses. "AI for all, le pouvoir au peuple" - c'est ce mantra qui m'a donné la force de me lancer dans cette aventure. Un simple microcontrôleur, quelques capteurs et un modèle TinyLM suffisent pour rendre accessible ce qui semblait auparavant réservé aux grandes institutions.
            </motion.p>
            
            <motion.div className="flex justify-center" variants={cardVariants}>
              <img 
                src="/images/kits/hypersuit-structure.png" 
                alt="Structure et composants principaux du prototype Hypersuit" 
                className="w-full max-w-2xl h-auto rounded-lg shadow-md"
              />
            </motion.div>
          </div>
        </motion.section>

        {/* Section 3: Notre vision */}
        <motion.section
          className="py-12 md:py-16 bg-gray-100"
          variants={sectionVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <div className="container mx-auto px-4">
            <motion.h2 className="text-3xl md:text-4xl font-bold text-gray-800 text-center mb-10 md:mb-12" variants={cardVariants}>
              Ma vision : une technologie <span className="text-indigo-600">par et pour tous</span>
            </motion.h2>
            <motion.div className="max-w-3xl mx-auto mb-10" variants={cardVariants}>
              <p className="text-lg text-gray-600 mb-6">
                Je crois profondément qu'une seule personne déterminée, équipée des bons outils, peut changer la donne. Mon parcours personnel m'a démontré que grâce à l'open source et aux modèles TinyLM, chacun d'entre nous peut prototyper, tester et déployer des solutions d'assistance sans barrière technologique ni expertise coûteuse.
              </p>
            </motion.div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
              <motion.div 
                className="flex justify-center" 
                variants={cardVariants}
              >
                <img 
                  src="/images/kits/hypersuit-phase1.png" 
                  alt="Conception et prototypage initial de l'Hypersuit" 
                  className="w-full max-w-md h-auto rounded-lg shadow-md"
                />
              </motion.div>
              
              <motion.div 
                className="flex justify-center" 
                variants={cardVariants}
              >
                <img 
                  src="/images/kits/hypersuit-phase2.png" 
                  alt="Tests et amélioration du prototype Hypersuit" 
                  className="w-full max-w-md h-auto rounded-lg shadow-md"
                />
              </motion.div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              <motion.div className="bg-white p-6 rounded-lg shadow-md" variants={cardVariants}>
                <div className="flex items-center mb-4">
                  <div className="p-3 rounded-full bg-blue-100 mr-4">
                    <DeviceTabletIcon className="h-6 w-6 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800">Autonomie renforcée</h3>
                </div>
                <p className="text-gray-600 mb-2">Analyses en temps réel, retours haptiques ou visuels pour aider les utilisateurs à regagner leur indépendance.</p>
              </motion.div>
              
              <motion.div className="bg-white p-6 rounded-lg shadow-md" variants={cardVariants}>
                <div className="flex items-center mb-4">
                  <div className="p-3 rounded-full bg-green-100 mr-4">
                    <BoltIcon className="h-6 w-6 text-green-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800">Micro-torque</h3>
                </div>
                <p className="text-gray-600 mb-2">Détection de changements de couple au milli-newton.mètre, permettant d'identifier les plus petites intentions de mouvement.</p>
              </motion.div>
              
              <motion.div className="bg-white p-6 rounded-lg shadow-md" variants={cardVariants}>
                <div className="flex items-center mb-4">
                  <div className="p-3 rounded-full bg-purple-100 mr-4">
                    <CodeBracketIcon className="h-6 w-6 text-purple-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-800">Prototype artisanal</h3>
                </div>
                <p className="text-gray-600 mb-2">Scripts et pipelines publics, adaptables à tout besoin, permettant à chacun de construire sa propre solution.</p>
              </motion.div>
            </div>
          </div>
        </motion.section>

        {/* Section 4: Technologie & R&D */}
        <motion.section
          className="py-12 md:py-16 bg-white"
          variants={sectionVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <div className="container mx-auto px-4">
            <motion.h2 className="text-3xl md:text-4xl font-bold text-gray-800 text-center mb-10 md:mb-12" variants={cardVariants}>
              Technologie & R&D : <span className="text-green-600">l'artisanat numérique</span>
            </motion.h2>
            
            <motion.div className="flex justify-center mb-12" variants={cardVariants}>
              <img 
                src="/images/kits/hypersuit-ai.png" 
                alt="Visualisation du système IA de l'Hypersuit" 
                className="w-full max-w-2xl h-auto rounded-lg shadow-lg"
              />
            </motion.div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
              {/* Carte 1: Acquisition légère */}
              <motion.div className="rounded-xl shadow-lg overflow-hidden bg-white flex flex-col" variants={cardVariants}>
                <div className="h-24 bg-gradient-to-r from-blue-500 to-blue-700 flex items-center justify-center">
                  <CpuChipIcon className="h-12 w-12 text-white opacity-80" />
                </div>
                <div className="p-5 flex-grow">
                  <h3 className="text-xl font-semibold text-gray-800 mb-3">Acquisition légère</h3>
                  <ul className="list-disc list-inside text-sm text-gray-500 space-y-2">
                    <li>Capteurs IMU et capteurs de couple low-cost</li>
                    <li>Microcontrôleurs Arduino, Raspberry Pi Pico pour streaming continu</li>
                  </ul>
                </div>
              </motion.div>

              {/* Carte 2: Prétraitement minimaliste */}
              <motion.div className="rounded-xl shadow-lg overflow-hidden bg-white flex flex-col" variants={cardVariants}>
                <div className="h-24 bg-gradient-to-r from-green-500 to-green-700 flex items-center justify-center">
                  <CircleStackIcon className="h-12 w-12 text-white opacity-80" />
                </div>
                <div className="p-5 flex-grow">
                  <h3 className="text-xl font-semibold text-gray-800 mb-3">Prétraitement minimaliste</h3>
                  <ul className="list-disc list-inside text-sm text-gray-500 space-y-2">
                    <li>Scripts Python (NumPy, pandas) pour normaliser et segmenter</li>
                    <li>Automatisation via makefiles ou Bash</li>
                  </ul>
                </div>
              </motion.div>

              {/* Carte 3: TinyLM embarqué */}
              <motion.div className="rounded-xl shadow-lg overflow-hidden bg-white flex flex-col" variants={cardVariants}>
                <div className="h-24 bg-gradient-to-r from-purple-500 to-purple-700 flex items-center justify-center">
                  <ServerIcon className="h-12 w-12 text-white opacity-80" />
                </div>
                <div className="p-5 flex-grow">
                  <h3 className="text-xl font-semibold text-gray-800 mb-3">TinyLM embarqué</h3>
                  <ul className="list-disc list-inside text-sm text-gray-500 space-y-2">
                    <li>Fine-tuning sur séries temporelles terrain</li>
                    <li>Quantification et déploiement direct sur microcontrôleur</li>
                  </ul>
                </div>
              </motion.div>

              {/* Carte 4: Boucle d'amélioration */}
              <motion.div className="rounded-xl shadow-lg overflow-hidden bg-white flex flex-col" variants={cardVariants}>
                <div className="h-24 bg-gradient-to-r from-orange-500 to-orange-700 flex items-center justify-center">
                  <CommandLineIcon className="h-12 w-12 text-white opacity-80" />
                </div>
                <div className="p-5 flex-grow">
                  <h3 className="text-xl font-semibold text-gray-800 mb-3">Boucle d'amélioration</h3>
                  <ul className="list-disc list-inside text-sm text-gray-500 space-y-2">
                    <li>Tests en conditions réelles</li>
                    <li>Réentraînement incrémental</li>
                    <li>Mises à jour via GitHub Actions</li>
                  </ul>
                </div>
              </motion.div>
            </div>
          </div>
        </motion.section>

        {/* Section 5: Matériel & logiciels */}
        <motion.section
          className="py-12 md:py-16 bg-gray-100"
          variants={sectionVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <div className="container mx-auto px-4">
            <motion.h2 className="text-3xl md:text-4xl font-bold text-gray-800 text-center mb-10" variants={cardVariants}>
              Matériel & logiciels <span className="text-orange-600">abordables</span>
            </motion.h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
              <motion.div className="bg-white p-8 rounded-xl shadow-lg" variants={cardVariants}>
                <h3 className="text-xl font-semibold text-gray-800 mb-6">Tout ce dont vous avez besoin :</h3>
                <div className="grid grid-cols-1 gap-6">
                  <ul className="list-disc list-inside text-gray-600 space-y-3">
                    <li>Arduino, Raspberry Pi Pico ou équivalent</li>
                    <li>Capteurs IMU et capteurs de couple</li>
                    <li>Environnement Python (VS Code, Jupyter)</li>
                    <li>TinyLM pour modèles embarqués</li>
                    <li>Git pour gestion de code</li>
                    <li>Quelques dizaines de francs suisses suffisent pour démarrer</li>
                  </ul>
                </div>
              </motion.div>
              
              <motion.div 
                className="flex items-center justify-center" 
                variants={cardVariants}
              >
                <img 
                  src="/images/kits/hypersuit-motor.png" 
                  alt="Composants matériels du prototype Hypersuit" 
                  className="w-full max-w-md h-auto rounded-lg shadow-md"
                />
              </motion.div>
            </div>
          </div>
        </motion.section>

        {/* Section 6: Pourquoi ce projet fait la différence */}
        <motion.section
          className="py-12 md:py-16 bg-white"
          variants={sectionVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <div className="container mx-auto px-4">
            <motion.h2 className="text-3xl md:text-4xl font-bold text-gray-800 text-center mb-10" variants={cardVariants}>
              Pourquoi ce projet fait la <span className="text-purple-600">différence</span>
            </motion.h2>
            
            <motion.div className="flex justify-center mb-12" variants={cardVariants}>
              <img 
                src="/images/kits/hypersuit-health.png" 
                alt="Hypersuit en application pour l'aide à la mobilité" 
                className="w-full max-w-2xl h-auto rounded-lg shadow-lg"
              />
            </motion.div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 max-w-4xl mx-auto">
              <motion.div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-blue-500" variants={cardVariants}>
                <h3 className="text-xl font-semibold text-gray-800 mb-3">Artisanal & transparent</h3>
                <p className="text-gray-600">Chaque étape documentée et partagée, permettant une compréhension totale du processus.</p>
              </motion.div>
              
              <motion.div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-green-500" variants={cardVariants}>
                <h3 className="text-xl font-semibold text-gray-800 mb-3">Accessible à tous</h3>
                <p className="text-gray-600">Matériel grand public et budgets réduits, rendant la technologie d'assistance à la portée de chacun.</p>
              </motion.div>
              
              <motion.div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-orange-500" variants={cardVariants}>
                <h3 className="text-xl font-semibold text-gray-800 mb-3">Rapide à prototyper</h3>
                <p className="text-gray-600">Itérations en quelques heures, permettant d'adapter rapidement la solution aux besoins spécifiques.</p>
              </motion.div>
              
              <motion.div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-purple-500" variants={cardVariants}>
                <h3 className="text-xl font-semibold text-gray-800 mb-3">Centricité utilisateur</h3>
                <p className="text-gray-600">Né d'un besoin réel pour l'autonomie des aînés, conçu avec et pour les utilisateurs finaux.</p>
              </motion.div>
            </div>
          </div>
        </motion.section>

        {/* Section 7: Votre défi */}
        <motion.section
          className="py-12 md:py-16 bg-gray-100"
          variants={sectionVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <div className="container mx-auto px-4">
            <motion.h2 className="text-3xl md:text-4xl font-bold text-gray-800 text-center mb-10" variants={cardVariants}>
              Votre défi : <span className="text-blue-600">rejoignez le mouvement</span>
            </motion.h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              <motion.div className="max-w-xl mx-auto" variants={cardVariants}>
                <div className="relative">
                  {/* Timeline line */}
                  <div className="absolute left-8 top-0 bottom-0 w-1 bg-blue-200 rounded-full"></div>
                  
                  {/* Step 1 */}
                  <div className="flex items-start mb-8 relative">
                    <div className="flex-shrink-0 mr-6 relative z-10">
                      <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center shadow-lg text-white text-2xl font-bold">
                        1
                      </div>
                    </div>
                    <div className="bg-white p-4 rounded-lg shadow-md flex-grow">
                      <h3 className="text-lg font-semibold text-gray-800 mb-2">Collectez votre matériel</h3>
                      <p className="text-gray-600">Rassemblez un microcontrôleur et quelques capteurs IMU standard.</p>
                    </div>
                  </div>
                  
                  {/* Step 2 */}
                  <div className="flex items-start mb-8 relative">
                    <div className="flex-shrink-0 mr-6 relative z-10">
                      <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center shadow-lg text-white text-2xl font-bold">
                        2
                      </div>
                    </div>
                    <div className="bg-white p-4 rounded-lg shadow-md flex-grow">
                      <h3 className="text-lg font-semibold text-gray-800 mb-2">Montez votre prototype</h3>
                      <p className="text-gray-600">Connectez les capteurs et configurez votre environnement de développement.</p>
                    </div>
                  </div>
                  
                  {/* Step 3 */}
                  <div className="flex items-start mb-8 relative">
                    <div className="flex-shrink-0 mr-6 relative z-10">
                      <div className="w-16 h-16 bg-purple-500 rounded-full flex items-center justify-center shadow-lg text-white text-2xl font-bold">
                        3
                      </div>
                    </div>
                    <div className="bg-white p-4 rounded-lg shadow-md flex-grow">
                      <h3 className="text-lg font-semibold text-gray-800 mb-2">Entraînez votre modèle</h3>
                      <p className="text-gray-600">Utilisez TinyLM pour adapter le modèle à vos besoins avec vos données.</p>
                    </div>
                  </div>
                  
                  {/* Step 4 */}
                  <div className="flex items-start relative">
                    <div className="flex-shrink-0 mr-6 relative z-10">
                      <div className="w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center shadow-lg text-white text-2xl font-bold">
                        4
                      </div>
                    </div>
                    <div className="bg-white p-4 rounded-lg shadow-md flex-grow">
                      <h3 className="text-lg font-semibold text-gray-800 mb-2">Testez et améliorez</h3>
                      <p className="text-gray-600">Déployez votre solution et continuez à l'améliorer avec les retours utilisateurs.</p>
                    </div>
                  </div>
                </div>
              </motion.div>
              
              <motion.div 
                className="flex items-center justify-center" 
                variants={cardVariants}
              >
                <img 
                  src="/images/kits/hypersuit-phase3.png" 
                  alt="Évolution et amélioration continue de l'Hypersuit" 
                  className="w-full max-w-md h-auto rounded-lg shadow-lg"
                />
              </motion.div>
            </div>
            
            <motion.div className="text-center mt-8" variants={cardVariants}>
              <p className="text-lg text-gray-700 italic">L'IA n'est plus réservée aux laboratoires ; elle vit dans chaque prototype artisanal.</p>
            </motion.div>
          </div>
        </motion.section>

        {/* Section 8: CTA Finale */}
        <motion.section
          className="py-16 md:py-20 bg-gradient-to-br from-blue-600 to-indigo-700 text-white"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <div className="container mx-auto px-4 text-center">
            <motion.h2 className="text-3xl md:text-4xl font-bold mb-8" variants={cardVariants}>
              Prêt à changer les choses ?
            </motion.h2>
            
            <motion.div 
              className="flex justify-center mb-10" 
              variants={cardVariants}
            >
              <img 
                src="/images/kits/hypersuit-explore.png" 
                alt="Impact et applications de l'Hypersuit" 
                className="w-full max-w-2xl h-auto rounded-lg shadow-xl"
              />
            </motion.div>
            
            <motion.div className="flex justify-center mb-8" variants={cardVariants}>
              <button
                onClick={() => navigate('/contact?subject=Hypersuit Project')}
                className="bg-white text-blue-600 font-semibold py-3 px-8 rounded-lg shadow-md hover:bg-gray-100 transition duration-300 flex items-center justify-center"
              >
                <UserGroupIcon className="h-5 w-5 mr-2" />
                <span>Contacter l'équipe R&D</span>
              </button>
            </motion.div>
          </div>
        </motion.section>

        {/* Footer Note */}
        <footer className="py-6 bg-gray-800 text-center text-gray-400 text-xs">
          <div className="container mx-auto px-4">
             <p>Projet Hypersuit de Minh-Tam Dang - Une initiative pour rendre l'autonomie accessible à tous.</p>
             <p>© {new Date().getFullYear()} Infinity Cloud. Tous droits réservés.</p>
          </div>
        </footer>
      </div>
    </>
  );
};

export default Hypersuit; 